import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import TextButton from '../buttons/TextButton';
import { useAuth } from '../../hooks/useAuth';
import useNetworkStatus from '../../hooks/useNetworkStatus';
import { useCookies } from 'react-cookie';

// Define the structure of lounge details
interface LoungeDetails {
  lounge_id: string;
  name: string;
  // Add other properties as needed
}

// Define the props for HeadingOne component
interface HeadingOneProps {
  showLogout?: boolean;
}

const HeadingOne: React.FC<HeadingOneProps> = ({ showLogout = true }) => {
  const navigate = useNavigate();
  const { handleLogout } = useAuth();
  const [loungeDetails, setLoungeDetails] = useState<LoungeDetails | null>(
    null,
  );
  const [currentDateTime, setCurrentDateTime] = useState<Date>(new Date());
  const [cookies, , removeCookie] = useCookies(['role', 'loungeDetails']);

  const isOnline = useNetworkStatus();
  const isInitialMount = useRef(true);
  const previousOnlineStatus = useRef(true);

  useEffect(() => {
    if (isInitialMount.current) {
      previousOnlineStatus.current = isOnline;
      isInitialMount.current = false;
    }
  }, [isOnline]);

  useEffect(() => {
    if (!isInitialMount.current) {
      if (isOnline && !previousOnlineStatus.current) {
        window.location.reload();
      }
      previousOnlineStatus.current = isOnline;
    }
  }, [isOnline]);

  const logout = async () => {
    try {
      handleLogout();
      removeCookie('role', { path: '/' });
      removeCookie('loungeDetails', { path: '/' });
      navigate('/login');
    } catch (error) {
      console.log('Error logging out:', error);
    }
  };

  useEffect(() => {
    const fetchLoungeDetails = async () => {
      const storedLoungeDetails = cookies.loungeDetails;
      if (storedLoungeDetails) {
        setLoungeDetails(storedLoungeDetails);
      }
    };

    // Initial fetch of lounge details
    fetchLoungeDetails();

    // Update current time every second
    const intervalId = setInterval(() => {
      setCurrentDateTime(new Date());
    }, 1000);

    // Cleanup interval on component unmount
    return () => clearInterval(intervalId);
  }, [cookies.loungeDetails]);

  const formatDateTime = (date: Date): string => {
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
  };

  return (
    <div className="flex w-full items-start justify-center">
      <div className="flex w-full flex-col items-start gap-1 text-sm font-medium text-[#111928] 2xl:text-lg">
        {cookies.role !== 'OfficeHead' && (
          <p className="text-[#111928]">
            Outlet ID: {loungeDetails ? loungeDetails.lounge_id : 'Loading...'}{' '}
            ({loungeDetails ? loungeDetails.name : 'Loading...'})
          </p>
        )}
        <p className="text-[#111928]">
          Date & Time: {formatDateTime(currentDateTime)}
        </p>
        {cookies.role !== 'OfficeHead' && (
          <p className="text-[#111928]">
            Internet status :{' '}
            <span className={isOnline ? 'text-green-500' : 'text-red-500'}>
              {isOnline ? 'Online' : 'Offline'}
            </span>{' '}
          </p>
        )}
      </div>
      {showLogout && (
        <div className="w-full">
          <TextButton
            className="ml-auto"
            text="Logout"
            onClick={logout}
            svg={
              <svg
                width="21"
                height="21"
                viewBox="0 0 21 21"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M9.77089 0.23999C12.2169 0.23999 14.2069 2.22999 14.2069 4.67599V5.60799C14.2069 6.02199 13.8709 6.35799 13.4569 6.35799C13.0429 6.35799 12.7069 6.02199 12.7069 5.60799V4.67599C12.7069 3.05599 11.3899 1.73999 9.77089 1.73999H4.89589C3.27889 1.73999 1.96289 3.05599 1.96289 4.67599V15.805C1.96289 17.424 3.27889 18.74 4.89589 18.74H9.78189C11.3939 18.74 12.7069 17.428 12.7069 15.816V14.873C12.7069 14.459 13.0429 14.123 13.4569 14.123C13.8709 14.123 14.2069 14.459 14.2069 14.873V15.816C14.2069 18.256 12.2209 20.24 9.78189 20.24H4.89589C2.45189 20.24 0.462891 18.251 0.462891 15.805V4.67599C0.462891 2.22999 2.45189 0.23999 4.89589 0.23999H9.77089ZM17.851 6.79399L20.779 9.70899C20.8051 9.73491 20.8285 9.76186 20.8499 9.79038L20.779 9.70899C20.8144 9.74392 20.8459 9.78209 20.8733 9.82279C20.8853 9.84124 20.8969 9.86031 20.9077 9.87991C20.9164 9.89522 20.9244 9.91121 20.9319 9.92745C20.9382 9.94181 20.9442 9.95617 20.9499 9.97076C20.9574 9.98977 20.964 10.0093 20.9698 10.0291C20.9742 10.0447 20.9782 10.0602 20.9817 10.076C20.9861 10.0951 20.9897 10.1143 20.9924 10.1337C20.994 10.1463 20.9955 10.1595 20.9967 10.1727C20.999 10.1956 21 10.2177 21 10.24L20.9949 10.302L20.9928 10.3417C20.9926 10.3434 20.9924 10.3451 20.9921 10.3468L21 10.24C21 10.2955 20.9938 10.3505 20.9819 10.4039C20.9782 10.4197 20.9742 10.4353 20.9697 10.4506C20.964 10.4707 20.9574 10.4902 20.95 10.5094C20.9442 10.5238 20.9382 10.5382 20.9317 10.5523C20.9244 10.5688 20.9164 10.5848 20.9078 10.6004C20.8969 10.6197 20.8853 10.6387 20.8729 10.6572C20.8659 10.6682 20.8582 10.679 20.8502 10.6896C20.8264 10.721 20.8004 10.7504 20.7723 10.7777L17.851 13.687C17.705 13.833 17.513 13.906 17.322 13.906C17.13 13.906 16.937 13.833 16.791 13.685C16.499 13.391 16.5 12.917 16.793 12.625L18.4329 10.99H8.20869C7.79469 10.99 7.45869 10.654 7.45869 10.24C7.45869 9.82599 7.79469 9.48999 8.20869 9.48999H18.4349L16.793 7.85599C16.5 7.56399 16.498 7.08999 16.791 6.79599C17.083 6.50199 17.557 6.50199 17.851 6.79399Z"
                  fill="#1C64F2"
                />
              </svg>
            }
          />
        </div>
      )}
    </div>
  );
};

export default HeadingOne;
